import { AlertTriangle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { DeltaChips } from './DraftPanel';
import type { AmDelta, DraftMove } from '../../lib/accountAnalytics';

// The two-step commit guard: the draft panel's Confirm opens this final-warning
// modal, and only its Reassign button actually writes. States the permanence
// plainly; lists the per-AM deltas + a scrollable move summary.
export function ConfirmReassignModal({
  open,
  moves,
  deltas,
  managerCount,
  committing,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  moves: DraftMove[];
  deltas: AmDelta[];
  managerCount: number;
  committing: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && !committing && onCancel()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            Confirm reassignment
          </DialogTitle>
          <DialogDescription>
            This permanently reassigns <strong>{moves.length}</strong> account
            {moves.length === 1 ? '' : 's'} across <strong>{managerCount}</strong> manager
            {managerCount === 1 ? '' : 's'} and <strong>cannot be undone</strong>.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <DeltaChips deltas={deltas} />
          <ul className="max-h-56 overflow-y-auto rounded-md border border-slate-200 text-sm">
            {moves.map((m) => (
              <li
                key={m.accountId}
                className="flex items-center justify-between gap-2 border-b border-slate-100 px-3 py-1.5 last:border-0"
              >
                <span className="truncate font-medium text-slate-800">{m.name}</span>
                <span className="shrink-0 whitespace-nowrap text-xs text-slate-500">
                  {m.fromAmLabel} <span className="text-slate-400">→</span>{' '}
                  <span className="font-medium text-kyck-700">{m.toAmLabel}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={committing}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={onConfirm} disabled={committing}>
            {committing ? 'Reassigning…' : `Reassign ${moves.length} account${moves.length === 1 ? '' : 's'}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
